import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  FormControl,
  FormHelperText,
  InputAdornment,
  MenuItem,
  Select,
  SvgIcon,
  TextField,
  Typography,
} from '@mui/material';
import React, { ReactElement, useState } from 'react';
import { Formik, FormikHelpers } from 'formik';

import {
  AppPrimaryButton,
  AppPrimaryButtonOutlined,
} from 'shared/components/AppComponents';

import { ReactComponent as Upload } from 'assets/images/ic_upload.svg';
import { TEVVService } from 'services/TEVVService';
import { Toast, Utils } from 'shared/helpers';
import { AISystemSchema } from 'validators/AISystemSchema';
import { BROADCAST_MESSAGES } from 'shared/Constants';
import { validateFile } from './fileValidation';

interface UploadFormValues {
  file: File | null;
  templateId: string;
  description: string;
}

const UploadModal = (props: any): ReactElement => {
  const {
    openDocumentModal,
    title,
    subtitle,
    cancelTitle,
    onCancel,
    successTitle,
    templateList,
    onSuccess,
  } = props;
  const [uploadForm, setUploadForm] = useState<UploadFormValues>({
    file: null,
    templateId: '',
    description: '',
  });

  const actionOnFormSubmit = (
    formValues: any,
    formik: FormikHelpers<any>
  ): void => {
    console.log(formValues);
    const formData = new FormData();
    formData.append('file', formValues.file);
    TEVVService.uploadTevvDocument(formValues.templateId, formData)
      .then((res) => {
        Utils.handleSuccessResponse(res);
        window.postMessage(BROADCAST_MESSAGES.RELOAD_AI_DOCUMENT_LIST);
      })
      .catch((error) => {
        Utils.handleErrorResponse(error);
        console.log(setUploadForm);
      })
      .finally(() => {
        formik.setSubmitting(false);
        onSuccess();
      });
  };

  return (
    <Dialog fullWidth maxWidth="sm" open={openDocumentModal} onClose={onCancel}>
      <Formik
        initialValues={uploadForm}
        onSubmit={(formValues, formik) => {
          actionOnFormSubmit(formValues, formik);
        }}
        validationSchema={AISystemSchema}
        enableReinitialize
        validateOnMount
      >
        {({
          values,
          handleChange,
          setFieldValue,
          handleSubmit,
          isValid,
          isSubmitting,
          touched,
          errors,
          handleBlur,
        }) => {
          const actionOnFileSelected = async (
            event: React.ChangeEvent<HTMLInputElement>
          ): Promise<void> => {
            const input = event.currentTarget;
            const file = input.files?.[0];

            if (!file) return;

            const isValid = await validateFile(file, Toast);

            if (!isValid) {
              input.value = '';
              return;
            }

            setFieldValue('file', file);
          };

          return (
            <>
              <Box padding={2}>
                <DialogContent className="dialog-content-container">
                  <Typography className="white" variant="h3">
                    {title}
                  </Typography>
                  <Box className="mt-2">
                    <Typography variant="h4">Select File</Typography>
                    <TextField
                      fullWidth
                      value={values.file ? values.file.name : 'No file chosen'}
                      placeholder="No file chosen"
                      onBlur={handleBlur}
                      error={touched.file && Boolean(errors.file)}
                      helperText={touched.file && errors.file}
                      InputProps={{
                        readOnly: true,
                        startAdornment: (
                          <InputAdornment position="start">
                            <Button component="label">
                              Choose File
                              <input
                                type="file"
                                hidden
                                // onChange={(e) => {
                                //   const file = e.target.files?.[0];
                                //   setFieldValue('file', file);
                                // }}
                                onChange={actionOnFileSelected}
                              />
                            </Button>
                          </InputAdornment>
                        ),
                      }}
                    />
                    <Typography
                      sx={{ mt: -3, color: 'error.main', fontSize: '11px' }}
                    >
                      Note: Password-protected and corrupted files are not
                      allowed.
                    </Typography>
                    <Typography variant="h4" mt={2}>
                      {subtitle} Type
                    </Typography>
                    <FormControl
                      fullWidth
                      error={touched.templateId && Boolean(errors.templateId)}
                    >
                      <Select
                        labelId="tevv-label"
                        name="templateId"
                        value={values.templateId}
                        onChange={handleChange}
                        displayEmpty
                        renderValue={(selected) => {
                          if (!selected) return `Select ${subtitle} Type`;

                          const selectedItem = templateList?.find(
                            (item: any) => item._id === selected
                          );

                          return selectedItem ? selectedItem.type : '';
                        }}
                        MenuProps={{
                          PaperProps: {
                            style: {
                              maxHeight: 300,
                              maxWidth: '300px',
                              overflowY: 'auto',
                            },
                          },
                        }}
                      >
                        <MenuItem value="" disabled>
                          Select {subtitle} Type
                        </MenuItem>
                        {templateList?.map((item: any) => (
                          <MenuItem key={item._id} value={item._id}>
                            <Box>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {item.category} - {item.stage}
                              </Typography>
                              <Typography variant="body1" fontWeight={500}>
                                {item.type}
                              </Typography>
                              <Typography
                                variant="body2"
                                color="text.secondary"
                              >
                                {item.description}
                              </Typography>
                            </Box>
                          </MenuItem>
                        ))}
                      </Select>
                      {touched.templateId && errors.templateId && (
                        <FormHelperText>{errors.templateId}</FormHelperText>
                      )}
                    </FormControl>
                    {/* <Typography variant="h4" mt={2}>
                    Description (Optional)
                  </Typography> */}

                    {/* <TextField
                    name="description"
                    value={values.description}
                    onChange={handleChange}
                    placeholder="Add a description for this document"
                    multiline
                    rows={4}
                    fullWidth
                  /> */}
                  </Box>
                </DialogContent>

                {/* Actions */}
                <DialogActions>
                  <AppPrimaryButtonOutlined
                    className="btn-small"
                    title={cancelTitle}
                    onClick={onCancel}
                  />

                  <AppPrimaryButton
                    className="btn-small"
                    type="submit"
                    onClick={handleSubmit}
                    isLoading={isSubmitting}
                    disabled={!isValid || isSubmitting}
                    startIcon={
                      <SvgIcon
                        component={Upload}
                        inheritViewBox
                        fontSize="inherit"
                        htmlColor="white"
                      />
                    }
                    title={successTitle}
                  />
                </DialogActions>
              </Box>
            </>
          );
        }}
      </Formik>
    </Dialog>
  );
};

export default UploadModal;
